// shared/pwa.ts: the installable-app rollout flag (docs/DECISIONS.md, "Installable app"). Pure; used by the Worker
// (manifest, service worker, site config), the SPA (registration, the kill switch) and scripts/check-config.ts.
//
// PWA_MODE is cumulative: "install" is the manifest, the icons and the service worker; "push" adds Web Push and
// "capture" the share target (later phases). "off" is the kill switch: no manifest, no registration, and every
// service worker already installed removes itself.

export const PWA_MODES = ["off", "install", "push", "capture"] as const;
export type PwaMode = (typeof PWA_MODES)[number];

/** A PWA_MODE value, or "off" for anything else (a missing or misspelled var never turns the app on). */
export function parsePwaMode(value: unknown): PwaMode {
  return typeof value === "string" && (PWA_MODES as readonly string[]).includes(value) ? (value as PwaMode) : "off";
}

/** Whether `mode` includes `level` ("capture" includes "push" and "install"). */
export function pwaAllows(mode: PwaMode, level: Exclude<PwaMode, "off">): boolean {
  return PWA_MODES.indexOf(mode) >= PWA_MODES.indexOf(level);
}

/** The paths the Worker answers before the asset layer (worker/src/app/pwa.ts); wrangler.jsonc run_worker_first lists them. */
export const APP_PATHS = ["/manifest.webmanifest", "/sw.js", "/offline.html", "/apple-touch-icon.png", "/apple-touch-icon-precomposed.png"] as const;

/** The offline page, and the header that marks it: the service worker never keeps anything else in its place. */
export const OFFLINE_URL = "/offline.html";
export const OFFLINE_MARKER = "x-hb-offline";

/**
 * /sw.js while PWA_MODE is off (served by worker/src/app/pwa.ts): it replaces whatever worker a phone has (browsers
 * check /sw.js on every navigation), deletes every cache of this origin, and unregisters. It has no fetch handler, so
 * nothing goes through it meanwhile. Here rather than in the Worker so the Node tests can run it
 * (tests/platform/sw-runtime.node.test.ts).
 */
export const KILL_SWITCH_SW = `// homebuild: the installed app is switched off (PWA_MODE=off). This worker clears the caches and unregisters.
self.addEventListener("install", function () { self.skipWaiting(); });
self.addEventListener("activate", function (event) {
  event.waitUntil(
    caches.keys()
      .then(function (keys) { return Promise.all(keys.map(function (key) { return caches.delete(key); })); })
      .then(function () { return self.registration.unregister(); })
  );
});
`;

/** Where the render script writes a tenant's app icons, and where the manifest points (public/ paths). */
export function appIconPaths(slug: string): { icon192: string; icon512: string; maskable512: string; apple180: string } {
  const dir = `/brand/${slug}/app`;
  return { icon192: `${dir}/icon-192.png`, icon512: `${dir}/icon-512.png`, maskable512: `${dir}/maskable-512.png`, apple180: `${dir}/apple-touch-icon.png` };
}
